import _ from 'lodash';

import chartUtils from './chartUtils';
import installLegend from './highchartsLegend';

const overlaps = (a, b) => a.left < b.right + 2 && b.left < a.right + 2 && a.top < b.bottom + 2 && b.top < a.bottom + 2;

function hideCoveredAxisLabels(chart) {
  (chart.axes || []).forEach(axis => {
    const boxes = (axis.plotLinesAndBands || [])
      .map(line => line.label && line.label.element)
      .filter(Boolean)
      .map(element => element.getBoundingClientRect())
      .filter(box => box.width && box.height);
    Object.keys(axis.ticks || {}).forEach(key => {
      const label = axis.ticks[key].label;
      if (!label || !label.element) {
        return;
      }
      const box = label.element.getBoundingClientRect();
      label.attr({ opacity: boxes.some(other => overlaps(box, other)) ? 0 : 1 });
    });
  });
}

function togglePlaceholder(chart) {
  const box = chart.renderTo;
  if (!box || !box.closest) {
    return;
  }
  const pageChart = !!box.closest('.chart_box_view') && !box.closest('.main.wallboard');
  const hiddenByLegend = !!(chart.series || []).length && chart.series.every(series => !series.visible);
  const shown = chart.container && chart.container.querySelector && chart.container.querySelector(':scope > .chart-placeholder');
  if (!pageChart && !hiddenByLegend && !shown) {
    return;
  }
  const card = box.closest('.box_white_shadow, .diagram_details');
  const loading = !!(card && Array.from(card.querySelectorAll('.chart-placeholder--loading')).some(item => !box.contains(item)));
  const empty = pageChart ? !loading && !(chart.series || []).some(series => series.visible && (series.points || []).some(
    point => point.visible !== false && point.y !== null && point.y !== undefined
  )) : hiddenByLegend;
  // drawn inside the chart's own container (already positioned): positioning the chart's box itself would let the cards'
  const layer = chart.container || box;
  let placeholder = Array.from(layer.children).find(child => child.classList.contains('chart-placeholder'));
  if (!empty) {
    if (placeholder) {
      placeholder.remove();
    }
    return;
  }
  const shape = chartUtils.shapeForChartType((chart.series[0] && chart.series[0].type) || chart.options.chart.type);
  if (!placeholder || placeholder.getAttribute('data-shape') !== shape) {
    if (placeholder) {
      placeholder.remove();
    }
    placeholder = document.createElement('div');
    placeholder.className = `chart-placeholder chart-placeholder--${shape}`;
    placeholder.setAttribute('data-shape', shape);
    placeholder.innerHTML = chartUtils.placeholderMarkup(shape);
    layer.appendChild(placeholder);
  }
  placeholder.style.top = `${chart.plotTop}px`;
  placeholder.style.left = `${chart.plotLeft}px`;
  placeholder.style.width = `${chart.plotWidth}px`;
  placeholder.style.height = `${chart.plotHeight}px`;
}

function spreadRightLineLabels(chart) {
  (chart.yAxis || []).forEach(axis => {
    const labels = (axis.plotLinesAndBands || [])
      .filter(item => item.label && item.label.alignAttr && item.options.label &&
        item.options.label.className === 'highcharts-plot-line-label-right')
      .map(item => ({ label: item.label, y: item.label.alignAttr.y, height: item.label.getBBox().height }))
      .sort((a, b) => b.y - a.y);
    for (let i = 1; i < labels.length; i++) {
      const below = labels[i - 1];
      const gap = below.height + 4;
      if (below.y - labels[i].y < gap) {
        labels[i].y = below.y - gap;
        labels[i].label.attr({ y: labels[i].y });
      }
    }
  });
}

// Highcharts' own date step rounds to the nearest step, which put a year of months 16px apart on the CFD
const DATE_LABEL_SPACE = 2; // in label widths
// a small card that can't keep that space for two dates (the Percentiles card: 3 months on 354px would show one date)
// keeps one label width
const DATE_LABEL_MIN_SPACE = 1;
const MIN_DATE_LABELS = 2;
const DAY_MS = 24 * 3600 * 1000;
// each step with its shortest length in days (a month from February, a quarter and a half year from the shortest run of
// months), so two labels are never closer than counted
const DATE_STEPS = [
  { unit: 'day', count: 1, days: 1 }, { unit: 'day', count: 2, days: 2 },
  { unit: 'week', count: 1, days: 7 }, { unit: 'week', count: 2, days: 14 },
  { unit: 'month', count: 1, days: 28 }, { unit: 'month', count: 3, days: 89 }, { unit: 'month', count: 6, days: 181 },
  { unit: 'year', count: 1, days: 365 }, { unit: 'year', count: 2, days: 730 }, { unit: 'year', count: 5, days: 1826 },
  { unit: 'year', count: 10, days: 3652 }
];
// where no step of "Aug 2026" labels shows two dates: "Aug" alone, from the chart's monthShort format
const SHORT_MONTH_STEPS = [{ unit: 'monthShort', count: 1, days: 28 }, { unit: 'monthShort', count: 3, days: 89 }];
// Highcharts' own lengths of its units, which its date ticks are given with
const UNIT_MS = { day: DAY_MS, week: 7 * DAY_MS, month: 28 * DAY_MS, monthShort: 28 * DAY_MS, year: 364 * DAY_MS };
// the averages cards format every unit as a month, so their weekly labels would read "Jul 2026, Jul 2026"
const COARSER_UNITS = { day: ['month', 'year'], week: ['month', 'year'], month: ['year'], year: [] };
// the widest date label: Poppins' digits are all one width and May is its widest month name ("28 May" 42.9px at 12px,
// "28 Jul" 35.3px)
const SAMPLE_LABEL_DATE = Date.UTC(2026, 4, 28);

function readableDateStep(pxPerMs, rangeMs, labelWidth) {
  const stepWithSpace = (space, steps = DATE_STEPS) => steps.find(({ unit, days }) =>
    days * DAY_MS * pxPerMs >= labelWidth(unit) * (1 + space));
  const showsDates = step => step && rangeMs >= MIN_DATE_LABELS * step.days * DAY_MS;
  const preferred = stepWithSpace(DATE_LABEL_SPACE);
  let step = (showsDates(preferred) ? preferred : stepWithSpace(DATE_LABEL_MIN_SPACE) || preferred) ||
    DATE_STEPS[DATE_STEPS.length - 1];
  if (!showsDates(step)) {
    step = stepWithSpace(DATE_LABEL_MIN_SPACE, SHORT_MONTH_STEPS);
  }
  return showsDates(step) ? { unitRange: UNIT_MS[step.unit], count: step.count, unitName: step.unit } : null;
}

function dateLabelFormat(axis, unitName) {
  const formats = axis.options.dateTimeLabelFormats || {};
  return formats[unitName] && (formats[unitName].main || formats[unitName]);
}

function dateLabelWidth(axis, unitName) {
  const { chart } = axis;
  const format = dateLabelFormat(axis, unitName);
  const coarserUnits = COARSER_UNITS[unitName];
  if (coarserUnits && coarserUnits.some(coarser => dateLabelFormat(axis, coarser) === format)) {
    return Infinity;
  }
  if (!chart.renderer || typeof format !== 'string') {
    // can't measure: Infinity means "never pick this step", the safe direction
    return Infinity;
  }
  // the axis labels' classes, so the page's styles for them apply to the measured text
  const group = chart.renderer.g().attr({ class: `highcharts-axis-labels highcharts-${axis.coll.toLowerCase()}-labels` }).add();
  const label = chart.renderer.text(chart.time.dateFormat(format, SAMPLE_LABEL_DATE), 0, 0)
    .css(axis.options.labels.style || {}).add(group);
  const width = label.getBBox().width;
  group.destroy();
  // getBBox returns 0 in a display:none container, NaN/undefined in jsdom; treat as "can't measure"
  if (!(width > 0) || !Number.isFinite(width)) {
    return Infinity;
  }
  return width;
}

function edgePaleMarks(Highcharts) {
  const proto = Highcharts && Highcharts.Series && Highcharts.Series.prototype;
  if (!proto || proto.hasPaleMarkEdges) {
    return;
  }
  proto.hasPaleMarkEdges = true;
  Highcharts.addEvent(Highcharts.Series, 'afterSetOptions', function edgePaleSeries(e) {
    const { options } = e;
    const edge = chartUtils.paleMarkEdge(options.color);
    if (!edge) {
      return;
    }
    const own = this.userOptions;
    if (this.type === 'scatter' && !(own.marker && own.marker.lineColor)) {
      options.marker = {
        ...options.marker,
        lineColor: edge,
        lineWidth: 1,
        states: _.mapValues(options.marker.states, (state) => (chartUtils.paleMarkEdge(state.lineColor) ? { ...state, lineColor: edge } : state)),
      };
    } else if ((this.type === 'column' || this.type === 'bar') && !own.borderColor) {
      options.borderColor = edge;
      options.borderWidth = 1;
    }
  });
}

function onShortMonths(axis) {
  const { info } = axis.tickPositions || {};
  return Boolean(info) && info.unitName === 'monthShort';
}

function addReadableDates(Highcharts) {
  if (!Highcharts || !Highcharts.addEvent || !Highcharts.Axis || Highcharts.naveReadableDatesAdded) {
    return;
  }
  Highcharts.naveReadableDatesAdded = true;
  Highcharts.addEvent(Highcharts.Axis, 'afterSetOptions', function() {
    const axis = this;
    if (!axis.isXAxis || !axis.dateTime) {
      return;
    }
    axis.dateTime.normalizeTimeTickInterval = function(tickInterval, units) {
      const own = Object.getPrototypeOf(this).normalizeTimeTickInterval.call(this, tickInterval, units);
      const pxPerMs = axis.len / (axis.max - axis.min);
      // Highcharts asks with the axis' tickInterval for the major ticks and with minorTickInterval for the minor ones
      if (tickInterval !== axis.tickInterval || !(pxPerMs > 0) || !Number.isFinite(pxPerMs)) {
        return own;
      }
      const widths = {};
      const step = readableDateStep(pxPerMs, axis.max - axis.min, unit => {
        if (widths[unit] === undefined) {
          widths[unit] = dateLabelWidth(axis, unit);
        }
        return widths[unit];
      });
      if (!step) {
        return own;
      }
      // never finer than Highcharts' own step: it keeps a chart's tickInterval and the data's spacing (minTickInterval)
      return step.unitRange * step.count < own.unitRange * own.count ? own : step;
    };
  });
  // Highcharts 9.3 gives a tick a larger unit's format (tickPositions.info.higherRanks) only on hour axes
  Highcharts.addEvent(Highcharts.Axis, 'afterSetTickPositions', function() {
    const axis = this;
    const ticks = axis.tickPositions;
    if (!onShortMonths(axis)) {
      return;
    }
    ticks.forEach((pos, i) => {
      if (i === 0 || axis.chart.time.dateFormat('%m', pos) === '01') {
        ticks.info.higherRanks[pos] = 'monthShortYear';
      }
    });
  });
  // Highcharts 9.3 can draw a year label on the first or last month past the chart's edge or over its neighbour
  Highcharts.addEvent(Highcharts.Chart, 'afterSetChartSize', function() {
    const { spacingBox, time } = this;
    const left = spacingBox.x;
    const right = spacingBox.x + spacingBox.width;
    this.xAxis.filter(onShortMonths).forEach(axis => {
      const ticks = axis.tickPositions;
      const { list } = axis.options.dateTimeLabelFormats.monthShortYear;
      const drawnAt = tick => {
        const { width } = tick.label.getBBox();
        const start = Math.min(Math.max(axis.toPixels(tick.pos) - width / 2, left), right - width);
        return { start, end: start + width };
      };
      [[0, 1], [ticks.length - 1, ticks.length - 2]].forEach(([edge, next]) => {
        const tick = axis.ticks[ticks[edge]];
        const neighbour = axis.ticks[ticks[next]];
        if (!tick || !tick.label || !neighbour || !neighbour.label) {
          return;
        }
        const x = axis.toPixels(tick.pos);
        const atStart = edge < next;
        const room = axis.getSlotWidth(tick) / 2 + (atStart ? x - left : right - x);
        const fits = () => {
          const label = drawnAt(tick);
          const other = drawnAt(neighbour);
          const gap = atStart ? other.start - label.end : label.start - other.end;
          return label.end - label.start <= room && gap >= 2 * axis.options.labels.padding;
        };
        const texts = list.map(format => time.dateFormat(format, tick.pos));
        for (let i = texts.indexOf(tick.label.textStr) + 1; i < texts.length && !fits(); i += 1) {
          tick.label.attr({ text: texts[i] });
        }
      });
    });
  });
}

const SMALL_SLICE_PERCENTAGE = 1;

function smallSliceConnector(labelPosition, connectorPosition, options) {
  if (this.percentage < SMALL_SLICE_PERCENTAGE) {
    return [];
  }
  return this.connectorShapes.fixedOffset.call(this, labelPosition, connectorPosition, options);
}

// Highcharts keeps a tooltip within the chart's width (#6659) but not its height, and an outside tooltip within the
// page rather than the window, so a long list of items ran past the bottom of the screen
const WHEEL_LINE = 16; // px of one "line" of a mouse wheel that counts lines, not pixels
const TOOLTIP_SCROLL = 'tooltip-list-scroll';
const TOOLTIP_SCROLL_CUT = 'tooltip-list-scroll--cut';
const TOOLTIP_CUT_PADDING = 16; // must match _diagram.scss .tooltip-list-scroll--cut padding-bottom and mask stop

function visibleBox(element) {
  let box = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
  for (let node = element; node && node !== document.body; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
      const rect = node.getBoundingClientRect();
      box = {
        left: Math.max(box.left, rect.left),
        top: Math.max(box.top, rect.top),
        right: Math.min(box.right, rect.right),
        bottom: Math.min(box.bottom, rect.bottom)
      };
    }
  }
  return box;
}

function containerArea(chart) {
  const { scaleX, scaleY } = chart.pointer.getChartPosition();
  const rect = chart.container.getBoundingClientRect();
  const view = visibleBox(chart.container);
  return {
    left: (view.left - rect.left) / scaleX,
    top: (view.top - rect.top) / scaleY,
    right: (view.right - rect.left) / scaleX,
    bottom: (view.bottom - rect.top) / scaleY,
    scaleX: 1,
    scaleY: 1,
    frame: 0
  };
}

// an outside tooltip is placed in the page's space, the others in the container's
function tooltipArea(tooltip) {
  const { chart } = tooltip;
  if (tooltip.outside) {
    const { scaleX, scaleY } = chart.pointer.getChartPosition();
    const left = window.pageXOffset;
    const top = window.pageYOffset;
    return {
      left, top, right: left + window.innerWidth, bottom: top + window.innerHeight, scaleX, scaleY,
      frame: (tooltip.options.borderWidth || 0) + 2 * (tooltip.distance || 0)
    };
  }
  return containerArea(chart);
}

// an outside tooltip on a scaled page is scaled about its centre (#11329)
function placeWithin(position, size, scale, start, end) {
  const shift = (size * (1 - scale)) / 2;
  return Math.max(start - shift, Math.min(position, end - shift - size * scale));
}

const TV_TOOLTIP_GAP = 24;
const TV_TOOLTIP_INSET = 16;

function tvTooltipPosition(tooltip, boxWidth, boxHeight, point, area) {
  const { chart } = tooltip;
  const chartPosition = chart.pointer.getChartPosition();
  const scaleX = tooltip.outside ? chartPosition.scaleX : 1;
  const scaleY = tooltip.outside ? chartPosition.scaleY : 1;
  const pointX = tooltip.outside ? chartPosition.left + scaleX * (point.plotX + chart.plotLeft) : point.plotX + chart.plotLeft;
  const pointY = tooltip.outside ? chartPosition.top + scaleY * (point.plotY + chart.plotTop) : point.plotY + chart.plotTop;
  const padding = (tooltip.outside ? tooltip.distance : 0) + (tooltip.options.useHTML && tooltip.label ? tooltip.label.padding : 0);
  const above = pointY - (TV_TOOLTIP_GAP + boxHeight + area.frame - padding) * scaleY;
  const below = pointY + (TV_TOOLTIP_GAP - padding) * scaleY;
  const fitsAbove = above >= area.top + TV_TOOLTIP_INSET;
  if (!fitsAbove && below + (boxHeight + area.frame) * scaleY > area.bottom - TV_TOOLTIP_INSET) {
    return null;
  }
  const top = fitsAbove ? above : below;
  const left = pointX - ((boxWidth + area.frame) * scaleX) / 2;
  return {
    x: left - ((boxWidth + area.frame) * (1 - scaleX)) / 2,
    y: top - ((boxHeight + area.frame) * (1 - scaleY)) / 2
  };
}

function plotRight(tooltip) {
  const { chart } = tooltip;
  const right = chart.plotLeft + chart.plotWidth;
  if (!tooltip.outside) {
    return right;
  }
  const chartPosition = chart.pointer.getChartPosition();
  return chartPosition.left + right * chartPosition.scaleX;
}

function tooltipScrollPart(content) {
  const existing = content.querySelector(`.${TOOLTIP_SCROLL}`);
  if (existing) {
    return existing;
  }
  const items = Array.from(content.children).filter(child => child.classList.contains('tooltip-list-item'));
  const nodes = items.length ? items : Array.from(content.childNodes);
  const part = document.createElement('div');
  part.className = TOOLTIP_SCROLL;
  content.insertBefore(part, nodes[0] || null);
  nodes.forEach(node => part.appendChild(node));
  return part;
}

function measureLabel(label) {
  label.text.getBBox(true);
  label.updateBoxSize();
}

function scrollTooltipOnWheel(chart) {
  const { container } = chart;
  if (!container || container.naveTooltipWheel) {
    return;
  }
  container.naveTooltipWheel = true;
  container.addEventListener('wheel', event => {
    const { tooltip } = chart;
    const part = tooltip && tooltip.naveScrollPart;
    if (!part || tooltip.isHidden || !part.isConnected || part.contains(event.target)) {
      return;
    }
    const before = part.scrollTop;
    part.scrollTop = before + (event.deltaMode === 1 ? event.deltaY * WHEEL_LINE : event.deltaY);
    if (part.scrollTop !== before) {
      event.preventDefault();
    }
  }, { passive: false });
}

function fitTooltipHeight(tooltip) {
  const { label } = tooltip;
  const content = label && label.text && label.text.element;
  tooltip.naveScrollPart = null;
  if (!content || !tooltip.options.useHTML) {
    return;
  }
  // Highcharts keeps the tooltip's HTML when its text is unchanged, so an earlier cut is undone before measuring
  const earlier = content.querySelector(`.${TOOLTIP_SCROLL_CUT}`);
  if (earlier) {
    earlier.classList.remove(TOOLTIP_SCROLL_CUT);
    earlier.style.maxHeight = earlier.naveMaxHeight;
    earlier.style.pointerEvents = '';
  }
  const area = tooltipArea(tooltip);
  const room = (area.bottom - area.top) / area.scaleY - area.frame;
  measureLabel(label);
  if (label.height <= room) {
    return;
  }
  const part = tooltipScrollPart(content);
  const row = part.firstElementChild ? part.firstElementChild.offsetHeight : 0;
  part.naveMaxHeight = part.style.maxHeight;
  part.classList.add(TOOLTIP_SCROLL_CUT);
  // a hidden outside tooltip stays where it was, see-through, so a list taking the mouse would cover the bars under it
  part.style.pointerEvents = tooltip.options.stickOnContact ? '' : 'none';
  measureLabel(label);
  part.style.maxHeight = `${Math.max(part.offsetHeight - (label.height - room), row + TOOLTIP_CUT_PADDING)}px`;
  measureLabel(label);
  tooltip.naveScrollPart = part;
  scrollTooltipOnWheel(tooltip.chart);
}

function keepTooltipsOnScreen(Highcharts) {
  if (!Highcharts || !Highcharts.wrap || !Highcharts.Tooltip || Highcharts.naveTooltipsOnScreen) {
    return;
  }
  Highcharts.naveTooltipsOnScreen = true;
  const proto = Highcharts.Tooltip.prototype;
  Highcharts.wrap(proto, 'updatePosition', function(proceed, point) {
    fitTooltipHeight(this);
    return proceed.call(this, point);
  });
  Highcharts.wrap(proto, 'getPosition', function(proceed, boxWidth, boxHeight, point) {
    const area = tooltipArea(this);
    const onTv = !!(this.chart.renderTo && this.chart.renderTo.closest && this.chart.renderTo.closest('.main.wallboard'));
    const position = (onTv && tvTooltipPosition(this, boxWidth, boxHeight, point, area)) || proceed.call(this, boxWidth, boxHeight, point);
    const inset = onTv ? TV_TOOLTIP_INSET : 0;
    const right = onTv ? Math.min(area.right - inset, plotRight(this)) : area.right;
    return {
      x: placeWithin(position.x, boxWidth + area.frame, area.scaleX, area.left + inset, right),
      y: placeWithin(position.y, boxHeight + area.frame, area.scaleY, area.top + inset, area.bottom - inset)
    };
  });
  // Highcharts 9.3.3 stops an unfinished move only when the next move animates, which a move right after a hide doesn't
  Highcharts.wrap(proto, 'move', function(proceed, x, y, anchorX, anchorY) {
    clearTimeout(this.tooltipTimeout);
    return proceed.call(this, x, y, anchorX, anchorY);
  });
}

export default function (Highcharts) {
  // the Flow Metrics overview's cards never call setDefaults
  addReadableDates(Highcharts);
  keepTooltipsOnScreen(Highcharts);
  edgePaleMarks(Highcharts);
  return {
    setDefaults: function() {
      Highcharts = Highcharts || {
        setOptions: function() {}
      };
      installLegend(Highcharts);
      // naveReadableDatesAdded makes a second call free; closes the gap for callers
      // that rely on setDefaults to resolve the global
      addReadableDates(Highcharts);
      if (Highcharts.addEvent && Highcharts.Chart && !Highcharts.naveChartEventsAdded) {
        Highcharts.naveChartEventsAdded = true;
        Highcharts.addEvent(Highcharts.Chart, 'destroy', function() {
          if (this.naveResizeObserver) {
            this.naveResizeObserver.disconnect();
          }
        });
        Highcharts.addEvent(Highcharts.Chart, 'render', function() {
          const chart = this;
          hideCoveredAxisLabels(chart);
          spreadRightLineLabels(chart);
          togglePlaceholder(chart);
          if (!chart.naveResizeObserver && window.ResizeObserver && chart.renderTo && chart.renderTo.closest &&
            chart.renderTo.closest('.chart_box_view, .main.wallboard')) {
            chart.naveResizeObserver = new window.ResizeObserver(() => {
              if (!chart.renderTo || !chart.container) {
                return;
              }
              if (!chart.renderTo.offsetWidth || !chart.renderTo.offsetHeight) {
                chart.naveWasHidden = true;
                return;
              }
              if (chart.naveWasHidden) {
                chart.naveWasHidden = false;
                chart.setSize(null, null, false);
                return;
              }
              chart.reflow();
            });
            chart.naveResizeObserver.observe(chart.renderTo);
          }
          clearTimeout(chart.naveCoveredLabelsTimer);
          chart.naveCoveredLabelsTimer = setTimeout(() => chart.axes && hideCoveredAxisLabels(chart), 600);
        });
      }
      Highcharts.setOptions({
        chart: {
          style: {
            fontFamily: 'Poppins'
          },
          selectionMarkerFill: 'rgba(0, 64, 128, 0.08)',
          resetZoomButton: {
            relativeTo: 'chart',
            position: {
              align: 'right',
              verticalAlign: 'top',
              x: -36,
              y: 0
            },
            theme: {
              fill: '#FFFFFF',
              stroke: '#D5E7F2',
              'stroke-width': 1,
              r: 8,
              padding: 6,
              style: {
                color: '#001A33',
                fontSize: '12px',
                fontWeight: '500'
              },
              states: {
                hover: {
                  fill: '#EFF6FB',
                  stroke: '#D5E7F2',
                  style: {
                    color: '#001A33'
                  }
                }
              }
            }
          }
        },
        time: {
          useUTC: false
        },
        plotOptions: {
          series: {
            cropThreshold: 8000,
            turboThreshold: 8000
          },
          pie: {
            dataLabels: {
              connectorShape: smallSliceConnector
            }
          }
        }
      });
    },
    splitTooltipOverride: function() {
      Highcharts = Highcharts || {
        Tooltip: {
          prototype: {}
        }
      };
      Highcharts.Tooltip.prototype.renderSplit = function(labels, points) {
        var tooltip = this,
          boxes = [],
          chart = this.chart,
          ren = chart.renderer,
          options = this.options,
          headerHeight,
          tooltipLabel = this.getLabel(),
          H = Highcharts,
          each = (arr, callback) => arr.forEach(callback),
          pick = H.pick,
          headerTop,
          distributionBoxTop = chart.plotTop;

        // Graceful degradation for legacy formatters
        if (H.isString(labels)) {
          labels = [false, labels];
        }
        // Create the individual labels for header and points, ignore footer
        each(labels.slice(0, points.length + 1), function(str, i) {
          if (str !== false) {
            var point = points[i - 1] || {
                // use the crosshair label // Item 0 is the header. Instead of this, we could also
                isHeader: true,
                plotX: points[0].plotX
              },
              owner = point.series || tooltip,
              tt = owner.tt,
              series = point.series || {},
              colorClass = 'highcharts-color-' + pick(point.colorIndex, series.colorIndex, 'none'),
              target,
              x,
              bBox,
              boxWidth;

            // Store the tooltip referance on the series
            if (!tt) {
              owner.tt = tt = ren
                .label(null, null, null, 'rect', null, null, options.useHTML)
                .addClass('highcharts-tooltip-box ' + colorClass)
                .attr({
                  padding: options.padding,
                  r: options.borderRadius,
                  /*= if (build.classic) { =*/
                  fill: options.backgroundColor,
                  stroke: options.borderColor || point.color || series.color || '${palette.neutralColor80}',
                  'stroke-width': options.borderWidth,
                  zIndex: options.zIndex || 10
                  /*= } =*/
                })
                .add(tooltipLabel);
            }

            tt.isActive = true;
            tt.attr({
              text: str
            });
            /*= if (build.classic) { =*/
            tt.css(options.style).shadow(options.shadow);
            /*= } =*/

            // Get X position now, so we can move all to the other side in
            // case of overflow
            bBox = tt.getBBox();
            boxWidth = bBox.width + tt.strokeWidth();
            if (point.isHeader) {
              headerHeight = bBox.height;
              if (chart.xAxis[0].opposite) {
                headerTop = true;
                distributionBoxTop -= headerHeight;
              }
              x = Math.max(
                0, // No left overflow
                Math.min(
                  point.plotX + chart.plotLeft - boxWidth / 2,
                  // No right overflow (#5794)
                  chart.chartWidth +
                    // Scrollable plot area
                    (chart.scrollablePixels ? chart.scrollablePixels - chart.marginRight : 0) -
                    boxWidth
                )
              );
            } else {
              x = point.plotX + chart.plotLeft - pick(options.distance, 16) - boxWidth;
            }

            let leftAligned = false;
            let moveCoefficient = boxWidth > 200 ? 2 : 1;
            if (x + boxWidth + chart.plotLeft * moveCoefficient >= chart.plotWidth - chart.plotLeft * moveCoefficient) {
              leftAligned = true;
            }

            // Prepare for distribution
            target = (point.series && point.series.yAxis && point.series.yAxis.pos) + (point.plotY || 0);
            target -= distributionBoxTop;

            if (point.isHeader) {
              target = headerTop ? -headerHeight : chart.plotHeight + headerHeight;
            }
            boxes.push({
              moveCoefficient: moveCoefficient,
              leftAligned: leftAligned,
              target: target,
              rank: point.isHeader ? 1 : 0,
              size: owner.tt.getBBox().height + 1,
              width: owner.tt.getBBox().width + 1,
              point: point,
              x: x,
              tt: tt
            });
          }
        });

        // Clean previous run (for missing points)
        this.cleanSplit();

        // Distribute and put in place
        H.distribute(boxes, chart.plotHeight + headerHeight);
        const area = containerArea(chart);
        each(boxes, function(box) {
          var point = box.point,
            series = point.series;

          // Put the label in place
          box.tt.attr({
            x: (function() {
              if (point.isHeader || box.leftAligned) {
                return box.x;
              }

              return point.plotX + chart.plotLeft + pick(options.distance, 16);
            })(),
            y: (function() {
              if (box.leftAligned && !point.isHeader) {
                return box.pos;
              }
              return box.pos + chart.plotTop;
            })(),
            anchorX: point.isHeader ? point.plotX + chart.plotLeft : point.plotX + series.xAxis.pos,
            anchorY: point.isHeader ? box.pos + chart.plotTop - 15 : point.plotY + series.yAxis.pos
          });
          box.tt.attr({
            x: placeWithin(box.tt.x, box.width, 1, area.left, area.right),
            y: placeWithin(box.tt.y, box.size, 1, area.top, area.bottom)
          });
        });
      };
    },
    plotlinesTooltip: function(text) {
      return {
        mouseover: function(e) {
          const series = this.axis.series[0],
            chart = series.chart,
            PointClass = series.pointClass,
            tooltip = chart.tooltip,
            point = new PointClass().init(series, ['Plotline', this.options.value]),
            normalizedEvent = chart.pointer.normalize(e);

          point.tooltipPos = [normalizedEvent.chartX - chart.plotLeft, normalizedEvent.chartY - chart.plotTop];
          point.text = text;
          tooltip.refresh(point);
        },
        mouseout: function(e) {
          this.axis.chart.tooltip.hide();
        }
      };
    },
    getFontSizeConfig: function(chart) {
      const baseConfig = {
        xAxis: [
          {
            labels: {
              style: {
                fontSize: '1.2em'
              }
            },
            title: {
              style: {
                fontSize: '1.3em'
              }
            }
          },
          {
            opposite: true,
            labels: {
              style: {
                fontSize: '1.2em'
              }
            },
            title: {
              style: {
                fontSize: '1.3em'
              }
            }
          }
        ],
        yAxis: {
          title: {
            style: {
              fontSize: '1.3em'
            }
          },
          labels: {
            style: {
              fontSize: '1.2em'
            }
          }
        }
      };

      const baseMasterConfig = {
        yAxis: {
          title: {
            style: {
              fontSize: '1.3em'
            }
          },
          labels: {
            style: {
              fontSize: '1.2em'
            }
          }
        },
        xAxis: [
          {
            title: {
              style: {
                fontSize: '1.3em'
              }
            },
            labels: {
              style: {
                fontSize: '1.2em'
              }
            }
          },
          {
            opposite: true,
            title: {
              style: {
                fontSize: '1.3em'
              }
            },
            labels: {
              style: {
                fontSize: '1.2em'
              }
            }
          }
        ]
      };

      switch (chart) {
        case 'percentile': {
          return {
            fontSize: '12px'
          };
        }
        case 'cfd': {
          return {
            chart: {
              ...baseConfig
            },
            master: {
              xAxis: {
                labels: {
                  style: {
                    fontSize: '1.2em'
                  }
                }
              }
            }
          };
        }
        case 'cta': {
          return {
            chart: {
              ...baseConfig
            },
            master: { ...baseMasterConfig }
          };
        }
        case 'cth':
        case 'th':
        case 'fe': {
          const config = {
            labels: {
              style: {
                fontSize: '1.2em'
              }
            },
            title: {
              style: {
                fontSize: '1.3em'
              }
            }
          };

          return {
            chart: {
              xAxis: config,
              yAxis: config
            },
            master: { ...baseMasterConfig }
          };
        }
        case 'ctsp':
        case 'ctb':
        case 'mc':
        case 'mct':
        case 'trc':
        case 'ddp': {
          return {
            chart: {
              ...baseConfig
            },
            master: { ...baseMasterConfig }
          };
        }
      }
    },
    updateSeries: function(data, chart) {
      const diff = _.cloneDeep(data);
      // Remove old values
      _.forEach(diff.arr1, s => {
        const serieIndex = _.findIndex(chart.series, {
          color: s.color,
          name: s.name
        });
        if (!!chart.series[serieIndex]) {
          _.forEach(s.data, p => {
            const chartPoints = _.filter(chart.series[serieIndex].data, d => d.x === p.x && d.y === p.y);
            _.forEach(chartPoints, cp => {
              cp.remove(false);
            });
          });
          if (!chart.series[serieIndex].data.length) {
            chart.series[serieIndex].remove(false);
          }
        }
      });

      // Insert/update new values
      _.forEach(diff.arr2, s => {
        const serieIndex = _.findIndex(chart.series, {
          color: s.color,
          name: s.name
        });
        if (!chart.series[serieIndex]) {
          chart.addSeries(s, false);
        } else {
          _.forEach(s.data, p => {
            const predicate = !!p.card ? d => d.card.id === p.card.id : d => d && d.x === p.x && d.y === p.y;
            const pointIndex = _.findIndex(chart.series[serieIndex].data, predicate);
            if (pointIndex > -1) {
              chart.series[serieIndex].data[pointIndex].update(p, false);
            } else {
              chart.series[serieIndex].addPoint(p, false);
            }
          });
        }
      });
    }
  };
};
